import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FaUserShield, FaChalkboardTeacher, FaUserGraduate, FaSync, FaEnvelope, FaLock, FaEye, FaEyeSlash, FaCheckCircle } from 'react-icons/fa';
import Spinner from '../common/Spinner';
import toast from 'react-hot-toast';

const FEATURES = [
    'QR Code & Barcode Attendance Scanning',
    'Role-Based Access — Admin, Staff & Student',
    'Automated PDF Attendance Reports',
    'Real-Time Email Notifications',
    'Event Creation & Draft Management',
    'Secure Password Reset via Email'
];

const generateCaptcha = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
};

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [role, setRole] = useState('admin');
    const [captcha, setCaptcha] = useState('');
    const [captchaText, setCaptchaText] = useState(generateCaptcha());
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (captcha !== captchaText) {
            toast.error('Invalid captcha!');
            setCaptchaText(generateCaptcha());
            setCaptcha('');
            return;
        }

        setLoading(true);
        const result = await login(email, password);
        setLoading(false);

        if (result.success) {
            navigate(`/${result.user.role}`);
        }
    };

    const refreshCaptcha = () => {
        setCaptchaText(generateCaptcha());
        setCaptcha('');
    };

    const roleTabs = [
        { id: 'admin', label: 'Admin', icon: <FaUserShield /> },
        { id: 'staff', label: 'Staff', icon: <FaChalkboardTeacher /> },
        { id: 'student', label: 'Student', icon: <FaUserGraduate /> }
    ];

    return (
        <div className="login-page-wrap">
            {/* Left branding panel - hidden on narrow screens */}
            <div className="login-left">
                <div className="login-left-content">
                    <div className="hero-logo">
                        <img src="/srm-logo.png" alt="SRMIST Logo" />
                    </div>
                    <h1 className="hero-uni">SRM Institute of Science and Technology</h1>
                    <p className="hero-campus">Delhi NCR Campus, Ghaziabad</p>
                    <div className="hero-feature-box">
                        <p className="hero-feature-title">📋 Attend360 — Event Attendance Management</p>
                        <ul className="hero-features">
                            {FEATURES.map((f) => (
                                <li key={f}><FaCheckCircle /> {f}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            {/* Right login panel */}
            <div className="login-right">
                <div className="login-card">
                    <div className="login-card-header">
                        <div className="login-logo">
                            <img src="/srm-logo.png" alt="SRMIST Logo" />
                        </div>
                        <div className="login-uni">SRM INSTITUTE OF SCIENCE AND TECHNOLOGY</div>
                        <div className="login-campus">DELHI - NCR CAMPUS, GHAZIABAD</div>
                    </div>

                    <h2 className="login-heading">Welcome Back</h2>
                    <p className="login-sub">Sign in to access your Attend360 dashboard</p>

                    <div className="login-tabs">
                        {roleTabs.map(({ id, label, icon }) => (
                            <button
                                key={id}
                                type="button"
                                onClick={() => setRole(id)}
                                className={`login-tab ${role === id ? 'active' : ''}`}
                            >
                                {icon} {label}
                            </button>
                        ))}
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="input-wrap">
                            <FaEnvelope className="input-icon" />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your registered email"
                                autoComplete="email"
                                required
                            />
                        </div>

                        <div className="input-wrap input-wrap-pass">
                            <FaLock className="input-icon" />
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                autoComplete="current-password"
                                required
                            />
                            <button
                                type="button"
                                className="toggle-pass"
                                onClick={() => setShowPassword(!showPassword)}
                                title={showPassword ? 'Hide password' : 'Show password'}
                            >
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>

                        <div className="captcha-box">
                            <span className="captcha-text">{captchaText}</span>
                            <button type="button" className="captcha-refresh" onClick={refreshCaptcha}>
                                <FaSync /> Refresh
                            </button>
                            <input
                                type="text"
                                value={captcha}
                                onChange={(e) => setCaptcha(e.target.value)}
                                placeholder="Enter captcha"
                                required
                            />
                        </div>

                        <button type="submit" className="login-btn" disabled={loading}>
                            {loading ? <><Spinner /> Signing in...</> : 'Sign In to Dashboard'}
                        </button>
                    </form>

                    <div className="login-hint">
                        <Link to="/forgot-password" className="btn-change-password">
                            Forgot Password?
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
